import { Link, useSearchParams } from "react-router";
import { effectiveTournamentStatus, tournamentInCurrentWeek } from "../../lib/tournamentTimeline";
import { SeasonCalendarPage } from "./SeasonCalendarPage";
import { TournamentsPage } from "./TournamentsPage";
import { LogisticsPage } from "./LogisticsPage";
import { TeamPage } from "./TeamPage";
import { useTournamentPreparation } from "./useTournamentPreparation";
import "./tournament-workspace.css";

type TournamentTab = "details" | "logistics" | "team";
const EVENT_TABS: Array<{ id: TournamentTab; label: string; description: string }> = [
  { id: "details", label: "Pulpit", description: "Gotowość i rundy" },
  { id: "team", label: "Obsada", description: "Ludzie i import" },
  { id: "logistics", label: "Logistyka", description: "Noclegi i auta" },
];
function isEventTab(value: string | null): value is TournamentTab { return EVENT_TABS.some((item) => item.id === value); }
function dateRange(start: string, end: string) {
  if (!start && !end) return "Termin nieustalony";
  if (!end || start === end) return start || end;
  return `${start} — ${end}`;
}

export function TournamentWorkspacePage() {
  const [params, setParams] = useSearchParams();
  const state = useTournamentPreparation();
  const tournamentId = params.get("tournament") || "";
  const calendarMode = !tournamentId && params.get("view") === "calendar";
  const requested = params.get("tab");
  const active: TournamentTab = isEventTab(requested) ? requested : "details";
  const selected = state.selectedTournament;
  const selectedStatus = selected ? effectiveTournamentStatus(selected) : "planned";
  const currentWeek = selected ? tournamentInCurrentWeek(selected) : false;

  function selectEventTab(tab: TournamentTab) {
    const next = new URLSearchParams(params);
    next.delete("view");
    if (tab === "details") next.delete("tab"); else next.set("tab", tab);
    setParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function selectCatalogMode(mode: "list" | "calendar") {
    const next = new URLSearchParams(params);
    next.delete("tournament");
    next.delete("tab");
    if (mode === "calendar") next.set("view", "calendar"); else next.delete("view");
    setParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (!tournamentId) {
    return (
      <section className="tournament-workspace-shell tournament-catalog-mode">
        <nav className="tournament-catalog-switch" aria-label="Widok turniejów">
          <button type="button" className={!calendarMode ? "active" : ""} onClick={() => selectCatalogMode("list")}><strong>Turnieje</strong><small>Bieżące, nadchodzące i archiwum</small></button>
          <button type="button" className={calendarMode ? "active" : ""} onClick={() => selectCatalogMode("calendar")}><strong>Kalendarz sezonu</strong><small>Widok wszystkich dat</small></button>
        </nav>
        <div className="tournament-workspace-content catalog-content">
          {calendarMode ? <SeasonCalendarPage /> : <TournamentsPage />}
        </div>
      </section>
    );
  }

  return (
    <section className="tournament-workspace-shell tournament-event-mode">
      <header className="tournament-event-command">
        <div className="tournament-event-command-main">
          <button type="button" className="tournament-event-back" onClick={() => selectCatalogMode("list")}>← Turnieje</button>
          <div>
            <div className="tournament-event-kicker"><span>{currentWeek && selectedStatus !== "completed" ? "TEN TYDZIEŃ" : selectedStatus === "completed" ? "ARCHIWUM" : "TURNIEJ"}</span><small>{selectedStatus === "active" ? "Aktywny" : selectedStatus === "completed" ? "Zakończony" : "Planowany"}</small></div>
            <h2>{selected?.name || "Wczytuję turniej…"}</h2>
            <p>{selected ? dateRange(selected.startDate, selected.endDate) : ""}</p>
          </div>
        </div>
        <div className="tournament-event-command-actions">
          <div className="tournament-event-context"><small>AKTYWNY KONTEKST</small><strong>{EVENT_TABS.find((item) => item.id === active)?.label}</strong><span>Turniej jest zablokowany do jawnego powrotu do listy.</span></div>
          <Link className="primary-button" to={`/generator?tournament=${encodeURIComponent(tournamentId)}&season=${state.season}`}>Otwórz Generator →</Link>
        </div>
      </header>

      <nav className="tournament-workspace-tabs" aria-label="Wybrany turniej — sekcje">
        {EVENT_TABS.map((item, index) => (
          <button key={item.id} type="button" className={`${active === item.id ? "active" : ""} ${EVENT_TABS.findIndex((tab) => tab.id === active) > index ? "visited" : ""}`} onClick={() => selectEventTab(item.id)}>
            <span>{index + 1}</span>
            <div><strong>{item.label}</strong><small>{item.description}</small></div>
          </button>
        ))}
      </nav>
      <div className={`tournament-workspace-content tab-${active}`}>
        {active === "details" ? <TournamentsPage /> : null}
        {active === "team" ? <TeamPage /> : null}
        {active === "logistics" ? <LogisticsPage /> : null}
      </div>
    </section>
  );
}
